import React from "react";
import { Container, Col, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { Component as Form } from "@repoflow/form-schema";
import * as Notify from "@repoflow/form-schema/notify";
import { Fields } from "@repoflow/components/fields";
import {
  Mutation,
  Button,
  useSectionContext,
} from "@repoflow/components/section";
import ContentTabs from "@repoflow/components/content-tabs";
import { Form as TypesForm } from "@repoflow/components/types-form";
import { ValuesPane, typesHelp } from "components/types-help";
import * as widgets from "widgets/register";
import { useWrite } from "forms/write";
import { TranslationsGrid } from "./translations";
import * as SiteQL from "queries/site";
import SitePublicSchema from "@common/schema/site/public";
import TypesSchema from "@common/schema/types";
import { types as paramTypes } from "@common/schema/types/params/utils";

// The site: what it is, and the document its pages are composed into, on one
// screen and behind one Save.
//
// It used to be three. `/site/:siteId` showed the row read-only with an Edit
// link beside a Remove; `/site/:siteId/edit` was a form holding the single
// field that link led to; `/site/:siteId/config` was the document shell in
// three tabs. A person changing a site's name and its `<title>` in the same
// sitting made two round trips through two screens and two writes of the same
// row — and `/Public/Site/Update` carries both fields, so each of those writes
// was already sending the half it did not show. The config screen has a test
// devoted to that: it had to hand the title back untouched or a save that
// reported success renamed the site to nothing.
//
// Joined, the second write stops existing. One `Section.Mutation` over
// `/Public/Site/Update`, four tabs that are views onto its one value object,
// and a Save that sends the whole update shape once. It is the shape the page
// editor and the section editor already have, and it is the same argument:
// splitting the save would let somebody leave with the head content saved and
// the name it belongs to not.
//
// The strip is four, and it was three: Details ABSORBED a tab — it held one
// field, and it was a field the tab beside it appeared to hold as well (see
// DETAILS_FIELDS for what the strip was hiding and what replaced it) — and the
// translation catalog then earned one, being the only thing on this screen that
// grows with the site rather than being set up once.
//
// Hoisted out of the render, all three of them, for the reason every field list
// in this app is: a `config` object literal in JSX is a new object each render,
// the form's SchemaProvider rebuilds its widget tree when that identity
// changes, and the control being typed into is detached between keystrokes.
// See P139.

// Read-only, above the tabs, because none of it is editable and all of it is
// true whichever tab is open. `siteId` is the range key — an input for it would
// read as a rename and would in fact write a second row (see
// /Public/Site/Update, where it is absent on purpose).
//
// `title` is NOT here any more: it is the first field on the Details tab now,
// and showing it twice on one screen — once as text, once as the input that
// changes it — is the ambiguity this merge exists to remove. That still holds
// with the tab strip a tab shorter: `siteId` is read-only because it is the
// key, not because of what it is next to.
const IDENTITY_TARGET = { $ref: "/Public/Site" };

const IDENTITY_FIELDS = {
  "/Public/Site": {
    fields: ["siteId", "createdAt", "updatedAt"],
  },
};

// The site's own field, first on the Details tab and above the document it
// belongs to.
//
// It had a tab of its own for one release, and the reason was the label.
// `/PageConfig` has a `title` too — the document's `<title>`, a different value
// written to a different half of the row — and two inputs both reading "Title"
// is exactly the confusion that made the Edit and Remove buttons dangerous a
// release ago. A tab strip renders one at a time, so the strip hid it.
//
// Hiding is not resolving. It cost a tab and a click to change a name, and the
// ambiguity was still there the moment anybody looked at both tabs in one
// sitting. Both are on one tab now and BOTH ARE RELABELLED — "Site title" here,
// "Document title" on the shell below, under a heading that says so — so the
// distinction is on screen rather than behind a click.
//
// They stay two fields. One `title` is what `/Public/Site/Update` requires and
// what the sites list shows; the other is what the composer puts in the
// document's head. Collapsing them into one input would silently drop whichever
// half lost, and which half that is would depend on the order the two forms
// happened to fire.
//
// Not "Site name", tempting as it reads: `siteId` is labelled "Name" already —
// on the read-only block directly above this and on the create form — and it is
// a third field again. "Title" is the word this app already uses for both of
// these, so the qualifier is what changes and the noun is what stays.
//
// ONE FIELD, and it held three for a release: `locales` and `defaultLocale`
// were here too, on the argument that a site declares its languages once and
// then leaves them alone. See LOCALES_FIELDS for why they are on Translations
// now. What is left here is the site's own title and the document below it,
// which is what the tab's name has always said it is.
const DETAILS_TARGET = { $ref: "/Public/Site/Update" };

const DETAILS_FIELDS = {
  "/Public/Site/Update": {
    // A screen-level `label:` overrides the schema's, which is where this has
    // to live: the same field renders on the sites list and the create form
    // too, where there is no document in sight and "Title" is unambiguous.
    // Qualifying it in common/schema/site/fields.js would rename it there as
    // well. Same argument as the `widget:` names below, on the other property a
    // screen config can carry.
    fields: [{ property: "title", label: "Site title" }],
  },
};

// THE LANGUAGES THE SITE PUBLISHES IN, on the tab that is about languages.
//
// They were the last two fields of the Details tab until this change, and what
// that arrangement missed is WHO edits them. The person who adds `es` is the
// person about to translate into it, and the column they are adding it for was
// a tab away: declare the language on Details, switch to Translations, type the
// strings. The grid's own empty state had to spell that trip out — "add them
// under Locales on the Details tab" — which is what a field on the wrong screen
// looks like from the screen that needs it.
//
// They belong to the same value object and the same Save wherever they render,
// so this is a move and not a new write path. What it buys is that the two
// halves of one job — which languages there are, and what the strings say in
// them — are on one tab, and the grid picks up a language the moment it is
// declared rather than on the next tab switch (see ./translations, `columnsOf`,
// which is the part of this that is not just a field list moving).
//
// A SECOND FORM over `/Public/Site/Update` rather than two more fields on the
// Details one. DetailsTab already has two forms rather than one nested config
// group, and the argument here is the same shape: each form hands back only the
// properties it owns, so a snapshot that goes stale cannot carry the other's
// half back. These two are never mounted together — ContentTabs keys its body
// on the selection — but the grid IS mounted beside this form, and both write
// through `useWrite` for it.
//
// Hoisted out of the render for P139 like every other field list in this file.
// Exported for the suite beside this one, which mounts what the screen mounts.
export const LOCALES_TARGET = { $ref: "/Public/Site/Update" };

export const LOCALES_FIELDS = {
  "/Public/Site/Update": {
    fields: [
      // No `widget:` on either. `locales` is an array of OBJECTS, which
      // form-schema already resolves to the list widget — unlike
      // `includesStyle` on the shell below, an array of strings, which resolves
      // to `tags` and is why that one has to name `list` by hand. Naming a
      // widget we do not need would be a demand on every other screen that
      // renders /Public/Site/Update.
      { property: "locales", layout: "wide" },
      // Under the list rather than over it, because it is an answer the list
      // above has to make available first: `defaultLocale` is a `/Site/Locale/Code`
      // and `model/site` refuses one the site does not declare.
      { property: "defaultLocale" },
    ],
  },
  // The row form inside the list widget. Present so the three fields appear in
  // the order an author thinks in — the code first, because it is what the rest
  // of the row is identified by.
  "/Site/Locale": {
    fields: ["code", "prefix", "label"],
    listing: ["code", "prefix", "label"],
  },
};

// WHERE A URL NOBODY PUBLISHES AT SENDS THE VISITOR — the fifth tab.
//
// A third form over `/Public/Site/Update`, owning exactly one property, for
// the reason the languages form gives: each form hands back only what it owns,
// so a snapshot that goes stale cannot carry another tab's half back. It is a
// tab of its own rather than rows on Details because, like the catalog, it is
// a thing that grows with the site rather than being set up once — and
// because the archived gen-1 screen had exactly this section
// (archive/recovered/pagews-admin-portal/src/root/admin/site/config/redirects),
// which is where the shape of a row comes from.
//
// No `widget:` — `redirects` is an array of OBJECTS, which form-schema
// resolves to the list widget on its own, exactly as `locales` does. The
// listing shows the two paths and not the two flags: a boolean renders as
// nothing useful in a table cell, and the row's own form is one click away.
export const REDIRECTS_TARGET = { $ref: "/Public/Site/Update" };

export const REDIRECTS_FIELDS = {
  "/Public/Site/Update": {
    fields: [{ property: "redirects", layout: "wide" }],
  },
  "/Site/Redirect": {
    fields: ["from", "to", "prefix", "permanent"],
    listing: ["from", "to"],
  },
};

// The catalog itself is not a form and has no field list: it is a grid, and the
// component that draws it is ./translations. See the header there for why the
// list widget was the wrong shape for the one list on this screen that grows.
//
// Nothing hoisted for P139 there, which is the pleasant part: there is no
// `config` object to rebuild per render, so there is nothing for a rebuilt
// SchemaProvider to detach.

// The document shell. Exported for the suite next to this file, which pins the
// two `widget:` names below against the schema they are applied to. A copy of
// the list in the test would pin the copy.
//
// Ported from the archived project's site config screen
// (`archive/recovered/stack-env/code/site-frontend/src/routes/domain/site/config`),
// which is the same gen-2 predecessor pages and sections came from. Two of its
// tabs are not here, and for different reasons now. "Operations" deleted every
// page and section of the site in one mutation — four unbounded per-partition
// iterators in a Promise.all, behind a screen that said it "might take a couple
// of minutes". This service does that cascade too, but nowhere near a mutation:
// Remove marks the site and a stream-triggered drain empties the tables behind
// it, resumably (docs/pagews.md §Deleting a site). So the tab stays absent
// because the operation it fronted is not one a person has to ask for, not
// because the capability is missing. "Copy from library" was already commented
// out there, against a library model this service does not have.
//
// The form targets `/PageConfig` and not `/Public/Site/Update`, which is what
// keeps it flat: nesting it under the site's update shape would render the
// whole thing inside one collapsible object group, one indent deeper, for no
// gain. The mutation still sends the update shape — see MutationHandler.
export const CONFIG_TARGET = { $ref: "/PageConfig" };

export const CONFIG_FIELDS = {
  "/PageConfig": {
    // Ordered as the document is: what identifies the page, then what wraps its
    // body, then what is injected around it. `types` and `values` are absent on
    // purpose — they have tabs of their own.
    fields: [
      // "Document title", not "Title", because the site's own title is now on
      // the same tab a few rows up rather than behind the strip. The label is
      // named here and not in common/schema/page-config/fields.js for the
      // reason given on DETAILS_FIELDS — and because every other screen that
      // renders /PageConfig, if one ever does, is a document screen where the
      // qualifier would be noise.
      { property: "title", label: "Document title" },
      "description",
      "faviconUrl",
      "bodyClasses",
      "bodyAttributes",
      { property: "bodyTemplate", layout: "wide" },
      { property: "metadataList", layout: "wide" },
      // `widget: "list"` on both, because the default for an array of string is
      // `tags` — a chip per entry, which reads as a keyword and edits as one:
      // no way to see a long CDN path in full, and a stray comma or space
      // splits one URL into two. These are ordered rows of one URL each, so
      // they get the row-per-entry widget the archived screen gave them
      // (`archive/recovered/stack-pkg/src/components/page-config/config/developer`
      // names it here too — the schema field carries no `widget:` there either,
      // for the same reason it carries none here: naming one in the schema is a
      // demand on every screen that renders the type).
      { property: "includesStyle", layout: "wide", widget: "list" },
      { property: "includesScript", layout: "wide", widget: "list" },
      { property: "attributes", layout: "wide" },
      { property: "inlineHead", layout: "wide" },
      { property: "inlineStyle", layout: "wide" },
      { property: "inlineScript", layout: "wide" },
    ],
  },
};

export const Sidebar = () => null;

export const Indice = () => null;

export const Content = () => {
  const {
    attributes: { siteId },
  } = useSectionContext();

  return (
    <Mutation
      target={DETAILS_TARGET}
      mutation={SiteQL.edit}
      update={SiteQL.update({ siteId })}
      // The site screens around this one read the site out of the query the
      // site route mounted with, and a client-side navigation does not re-run
      // it. This screen stays put after a save, so it is the SIBLING screens —
      // the breadcrumb's list, a page's preview — that would otherwise show the
      // old row.
      refetch={[{ query: SiteQL.get, variables: { siteId } }]}
      onMutate={({ handler, state: { values } }) => {
        handler({ variables: { siteId, input: values } });
      }}
      onCompleted={() => {
        Notify.success("Site saved");
      }}
      onError={(error) => {
        Notify.error(error.toString());
      }}
    >
      {MutationHandler}
    </Mutation>
  );
};

// One mutation, one Save, four tabs over the same form state.
//
// `values` is the site's update shape. Details holds TWO forms — the site's own
// title and the document shell — Values and Types hold one each, and
// Translations holds a form AND a grid: the languages the site declares, and
// the catalog of strings in them. Every one of them hands back only what it
// owns, which is what lets one write carry all four.
//
// THAT HANDING-BACK IS LOAD-BEARING IN A WAY IT WAS NOT WHILE THE TWO WERE ON
// SEPARATE TABS. A form-schema form seeds its internal state from `values`
// once, at mount, and never re-seeds — the effect that would is commented out
// in its section-state — so each form holds a SNAPSHOT of the object and every
// write it makes is a whole-object merge over that snapshot. ContentTabs keys
// its body on the selection, so switching tabs used to unmount one form and
// mount the other against fresh values; on one tab they are mounted together
// and neither is ever re-seeded from the other's writes. The site form's
// snapshot still carries the `config` it saw at mount, for the whole session.
//
// Neither form writes the other's half — the site form's onChange destructures
// `title` and drops the stale `config` its snapshot is still holding, and
// `setConfig` writes only `config` — and that is necessary but NOT sufficient,
// which is what `useWrite` (forms/write) is for.
const MutationHandler = ({ state, mutate, values: site, base }) => {
  const { values, setValues } = state;

  const write = useWrite(values, setValues);

  const config = values.config || {};
  const setConfig = (mod) => write({ config: mod });

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <div className="float-right">
            {/* Remove, and only Remove. Edit is gone because this screen IS the
                edit, and this is still the one place in the site where the
                button means the SITE — the bar above the tabs renders on a
                page's and a section's screens too, and the first e2e run after
                it carried an Edit clicked the wrong one and saved the site's
                title over the page's. */}
            <Link className="btn btn-danger" to={`${base}/remove`}>
              Remove
            </Link>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Fields
            schema={SitePublicSchema}
            values={site}
            widgets={widgets}
            target={IDENTITY_TARGET}
            config={IDENTITY_FIELDS}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <ContentTabs
            tabs={[
              {
                label: "Details",
                content: (
                  <DetailsTab
                    values={values}
                    write={write}
                    config={config}
                    setConfig={setConfig}
                    onSubmit={mutate}
                  />
                ),
              },
              {
                label: "Values",
                content: (
                  <ValuesPane
                    context="site"
                    types={config.types}
                    values={config.values}
                    widgets={widgets}
                    // The generated schema references /Image and friends by
                    // $ref, and a site's own types may too. Passing the site's
                    // public schema in means a param can use them without
                    // types-values knowing they exist.
                    utils={SitePublicSchema}
                    onChange={(mods) => setConfig({ ...config, values: mods })}
                  />
                ),
              },
              {
                // Its own tab rather than a block on Details, because the
                // catalog is the one thing on this screen that grows: a site's
                // languages are a handful of rows, its catalog is one row per
                // translated string per language. The languages themselves are
                // here too — they are what a column IS, and declaring one is
                // the first half of translating into it.
                label: "Translations",
                content: (
                  <TranslationsTab values={values} write={write} onSubmit={mutate} />
                ),
              },
              {
                // After Translations and before Types: both of the two that
                // grow with the site, then the one that is set up once.
                label: "Redirects",
                content: (
                  <RedirectsTab values={values} write={write} onSubmit={mutate} />
                ),
              },
              {
                label: "Types",
                content: (
                  <TypesForm
                    help={typesHelp("site")}
                    schema={TypesSchema}
                    paramTypes={paramTypes}
                    widgets={widgets}
                    values={{ types: config.types }}
                    onChange={({ types }) => setConfig({ ...config, types })}
                  />
                ),
              },
            ]}
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          {/* Below the tabs rather than inside the strip, the way it was on the
              configuration screen: this is one pane, so a Save under the tab
              content reads as saving the content — and it saves all four tabs,
              which is the point. */}
          <Button onClick={() => mutate()}>Save</Button>
        </Col>
      </Row>
    </Container>
  );
};

// The site's languages and the catalog of strings in them, in that reading
// order: the columns first, then the grid they are the columns of.
//
// TWO WRITERS ON ONE TAB, which is the arrangement DetailsTab documents at
// length and this one now shares. The languages form hands back `locales` and
// `defaultLocale`; the grid hands back `translations`; neither ever hands back
// the other's half or anything the two tabs beside this own. Both go through
// `write` and not `setValues` — a form-schema form pushes its value up from an
// effect, one commit behind the keystroke, so a plain merge over this render's
// `values` is what strands the other's write (forms/write, and
// docs/form-schema.md § Bite two).
//
// The languages form is a form and the catalog is not, and that asymmetry is
// the point rather than an inconsistency: a locale is a document with three
// properties, which is exactly what form-schema's list widget edits well, and a
// handful of rows is where a modal per row is the right trade. A CELL is
// (row, column) rather than a property of a document, and there are hundreds of
// them. See ./translations.
//
// The whole catalog at once rather than a picker per language, and that is the
// tab's argument for existing: a translator wants to see the strings, and one
// language at a time makes "which of these has no Spanish yet" a question you
// answer by clicking through every language rather than by looking.
//
// The three things the grid is given besides the catalog are all read from the
// SAME value object the mutation holds, and the first two are now written on
// this tab rather than the one beside it:
//
//   locales        the columns, live: the form above is what changes this, and
//                  the grid re-derives its declared columns from it on every
//                  render so a language becomes a column as soon as it is
//                  declared. A language the site does NOT declare has nowhere
//                  to be typed, which is one of the two rules `model/site`
//                  refuses a catalog for.
//   defaultLocale  which column is the fallback, so a row there reads as the
//                  deliberate override of the site's own text that it is.
//   config.values  the site's own text, shown beside its translations, and the
//                  set of strings that HAVE no row yet — which is what makes an
//                  untranslated string visible without anybody adding it first.
//                  Written on the Values tab, so it is current as of this
//                  tab's mount and cannot change under the grid.
export const TranslationsTab = ({ values, write, onSubmit }) => (
  <>
    <h6 className="text-muted mb-3">Languages</h6>
    <Form
      schema={SitePublicSchema}
      target={LOCALES_TARGET}
      widgets={widgets}
      // The whole update shape, not `{ locales, defaultLocale }`: the form
      // extracts what it is given against the target, so a value object missing
      // `config` would read as a change from the moment it mounted and write an
      // empty shell over the stored one before anybody touched a field. Same
      // argument as the site form on Details.
      values={values}
      // The two properties this form owns, and nothing else. Its snapshot
      // carries a `translations` that stops being current the moment the grid
      // below writes one, and `config` that has been stale since whenever this
      // tab was opened.
      onChange={({ locales, defaultLocale }) => write({ locales, defaultLocale })}
      onSubmit={onSubmit}
      config={LOCALES_FIELDS}
    />
    <h6 className="text-muted mt-4 mb-3">Catalog</h6>
    <p className="text-muted">
      One row per string, one column per language. A cell overrides that string
      for that language; an empty cell falls back to the site's own text, so a
      partial catalog is fine — and a page's own Values still win over both. A
      row only does anything if its name matches one of the site's Values.
    </p>
    <TranslationsGrid
      locales={values.locales}
      defaultLocale={values.defaultLocale}
      source={(values.config || {}).values}
      translations={values.translations}
      onChange={(translations) => write({ translations })}
      onSubmit={onSubmit}
    />
  </>
);

// The site's redirects, on a tab of their own. One form, one property, and
// the same `write` every other writer on this screen goes through — see the
// note on MutationHandler for why that is not `setValues`.
//
// The paragraph above the list is the rule the renderer applies, stated where
// the person declaring a row can read it, because none of it is visible from
// the form: a page on the same route wins, a prefix row carries the rest of
// the path, and the default is a 302.
export const RedirectsTab = ({ values, write, onSubmit }) => (
  <>
    <p className="text-muted">
      Where a visitor is sent when they ask for a path no page is published at.
      A page on the same path always wins. Tick "Everything below it too" to
      send a whole section elsewhere with the rest of the path carried over;
      the target is a path on this site or a full http(s) address. Redirects
      are temporary (302) unless marked permanent.
    </p>
    <Form
      schema={SitePublicSchema}
      target={REDIRECTS_TARGET}
      widgets={widgets}
      // The whole update shape, for the reason the two other forms over it
      // give: a value object missing `config` would read as a change from the
      // moment it mounted.
      values={values}
      // The one property this form owns.
      onChange={({ redirects }) => write({ redirects })}
      onSubmit={onSubmit}
      config={REDIRECTS_FIELDS}
    />
  </>
);

// The site and the document it is composed into, in that reading order: the
// row's own title first, then the shell, under a heading that says whose the
// fields below it are.
//
// Two forms rather than one over `/Public/Site/Update` with `config` as a
// field: that nests the whole shell inside one object group an indent deeper,
// under the schema's "Configuration" label, which is the shape the split
// avoided in the first place. Side by side they render flat, exactly as they
// did on two tabs.
//
// A component and not an inline fragment, and exported, for the same reason
// CONFIG_FIELDS is: the test beside this file mounts what the SCREEN mounts.
// The thing worth pinning here is the wiring — which form is handed which half
// and which half it hands back — and a harness that rebuilt that wiring would
// pin the rebuild. `config` and `setConfig` are passed in rather than derived
// so they are the same two the Values and Types tabs are given.
//
// `write` and not `setValues`: the two forms below are the only place in this
// screen where two writers of one object are mounted at once, so they are the
// reason it exists. See the note on MutationHandler.
export const DetailsTab = ({ values, write, config, setConfig, onSubmit }) => (
  <>
    <Form
      schema={SitePublicSchema}
      target={DETAILS_TARGET}
      widgets={widgets}
      // The whole update shape, not `{ title }`: the form extracts what it is
      // given against the target, so a value object missing `config` would read
      // as a change from the moment it mounted and write an empty shell over
      // the stored one before anybody touched a field.
      values={values}
      // `title` alone, deliberately, and it is the ONE property this form owns
      // again now that the languages are edited on Translations. This form's
      // snapshot carries a `config` that stops being current the moment the
      // form below writes one, and a `locales` that stops being current the
      // moment the tab beside it does; handing either back is how a write gets
      // reverted with nothing on screen to say so — see the note on
      // MutationHandler.
      onChange={({ title }) => write({ title })}
      onSubmit={onSubmit}
      config={DETAILS_FIELDS}
    />
    <h6 className="text-muted mt-4 mb-3">Document</h6>
    <Form
      schema={SitePublicSchema}
      target={CONFIG_TARGET}
      widgets={widgets}
      values={config}
      onChange={setConfig}
      onSubmit={onSubmit}
      config={CONFIG_FIELDS}
    />
  </>
);
